// pages/PublicEventsPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import enLocale from '@fullcalendar/core/locales/en-gb';
import { Calendar, Globe, Clock, Lock, Search, Filter, X, Plus, Image as ImageIcon, List, CalendarDays } from 'lucide-react';
import axios from 'axios';
import { RootState } from '../store/store';
import { selectUser } from '../store/slices/authSlice';
import { API_BASE_URL } from '../config/api';
import GuestAuthBanner from '../components/GuestAuthBanner';
import api from '../services/api';
import { getImageUrl } from '../utils/imageUtils';

interface EventData {
  _id: string;
  title: string;
  description: string;
  start: string;
  end: string;
  type: 'meeting' | 'workshop' | 'deadline' | 'social' | 'conference' | 'training' | 'webinar' | 'networking' | 'other';
  color: string;
  isPublic: boolean;
  image?: string;
  images?: string[];
  creator?: { _id: string; name: string; email: string };
}

const EVENT_TYPES = ['meeting', 'workshop', 'deadline', 'social', 'conference', 'training', 'webinar', 'networking', 'other'] as const;

const EVENT_TYPE_LABELS: Record<string, string> = {
  meeting: 'Meeting', workshop: 'Workshop', deadline: 'Deadline', social: 'Social Event',
  conference: 'Conference', training: 'Training', webinar: 'Webinar', networking: 'Networking', other: 'Other',
};

const EVENT_TYPE_COLORS: Record<string, string> = {
  meeting: '#3788d8', workshop: '#56cc9d', deadline: '#ff9f40', social: '#10b981',
  conference: '#0ea5e9', training: '#14b8a6', webinar: '#f59e0b', networking: '#ec4899', other: '#64748b',
};

function getEventColor(event: EventData): string {
  return event.color || EVENT_TYPE_COLORS[event.type] || EVENT_TYPE_COLORS.meeting;
}

const API_BASE = API_BASE_URL;

const PublicEventsPage: React.FC = () => {
  const user = useSelector((state: RootState) => selectUser(state));
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventData[]>([]);
  const [allEvents, setAllEvents] = useState<EventData[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<'all' | 'upcoming' | 'past'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '', description: '', start: '', end: '', type: 'meeting', isPublic: true
  });
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);

  useEffect(() => {
    const fetchPublicEvents = async () => {
      setEventsLoading(true);
      setEventsError(null);
      try {
        const res = await api.get('/events/all');
        const fetchedEvents = res.data.data || [];
        const publicEvents = user?.role === 'admin' ? fetchedEvents : fetchedEvents.filter((e: EventData) => e.isPublic);
        setAllEvents(publicEvents);
        setEvents(publicEvents);
      } catch (err: any) {
        const msg = err.response?.data?.error || err.message || 'Error loading events';
        setEventsError(msg);
      } finally {
        setEventsLoading(false);
      }
    };
    fetchPublicEvents();
  }, [user?.role]);

  const formatEventDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getTypeLabel = (type: string) => EVENT_TYPE_LABELS[type] || type;

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      meeting: 'bg-blue-100 text-blue-700', workshop: 'bg-emerald-100 text-emerald-700',
      deadline: 'bg-orange-100 text-orange-700', social: 'bg-emerald-100 text-emerald-700',
      conference: 'bg-sky-100 text-sky-700', training: 'bg-teal-100 text-teal-700',
      webinar: 'bg-amber-100 text-amber-700', networking: 'bg-pink-100 text-pink-700', other: 'bg-slate-100 text-slate-700',
    };
    return colors[type] || colors.other;
  };

  const handleEventClick = (eventId: string) => navigate(`/events/${eventId}`);
  const handleCalendarEventClick = (info: { event: { id: string } }) => {
    if (info.event.id) navigate(`/events/${info.event.id}`);
  };
  const handleKeyDown = (e: React.KeyboardEvent, eventId: string) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEventClick(eventId); }
  };

  useEffect(() => {
    let filtered = [...allEvents];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(e => e.title.toLowerCase().includes(q) || e.description?.toLowerCase().includes(q));
    }
    if (selectedType !== 'all') filtered = filtered.filter(e => e.type === selectedType);
    const now = new Date();
    if (dateFilter === 'upcoming') filtered = filtered.filter(e => new Date(e.start) >= now);
    else if (dateFilter === 'past') filtered = filtered.filter(e => new Date(e.end) < now);
    setEvents(filtered);
  }, [searchQuery, selectedType, dateFilter, allEvents]);

  const clearFilters = () => { setSearchQuery(''); setSelectedType('all'); setDateFilter('all'); };
  const hasActiveFilters = searchQuery.trim() !== '' || selectedType !== 'all' || dateFilter !== 'all';

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(f => f.size <= 5 * 1024 * 1024);
    if (files.length > 0) {
      setPhotoFiles(prev => [...prev, ...files]);
      files.forEach(f => {
        const reader = new FileReader();
        reader.onloadend = () => setPhotoPreviews(prev => [...prev, reader.result as string]);
        reader.readAsDataURL(f);
      });
    }
    (e.target as HTMLInputElement).value = '';
  };
  const handleRemovePhoto = (index: number) => {
    setPhotoFiles(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value }));
  };

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) { setCreateError('Please log in to create events'); return; }
    setCreating(true);
    setCreateError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) { setCreateError('Please log in to create events'); setCreating(false); return; }
      if (!formData.start || !formData.end) { setCreateError('Start and end dates are required'); setCreating(false); return; }
      const startDate = new Date(formData.start);
      const endDate = new Date(formData.end);
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) { setCreateError('Invalid date format'); setCreating(false); return; }
      if (endDate <= startDate) { setCreateError('End date must be after start date'); setCreating(false); return; }

      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title.trim());
      formDataToSend.append('description', (formData.description || '').trim());
      formDataToSend.append('start', startDate.toISOString());
      formDataToSend.append('end', endDate.toISOString());
      formDataToSend.append('type', formData.type);
      formDataToSend.append('isPublic', String(Boolean(formData.isPublic)));
      formDataToSend.append('creatorId', user._id);
      photoFiles.forEach(f => formDataToSend.append('images', f));

      const res = await axios.post(`${API_BASE}/events`, formDataToSend, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });

      if (res.data.success) {
        setFormData({ title: '', description: '', start: '', end: '', type: 'meeting', isPublic: true });
        setPhotoFiles([]);
        setPhotoPreviews([]);
        setShowCreateModal(false);
        setCreateError(null);
        alert(user.role === 'admin' ? 'Event created successfully!' : 'Event created successfully! It is pending admin approval.');
        window.location.reload();
      } else {
        setCreateError(res.data.error || 'Failed to create event');
      }
    } catch (err: any) {
      let errorMsg = 'Failed to create event';
      if (err.response?.status === 401) errorMsg = 'Please log in to create events';
      else if (err.response?.status === 403) errorMsg = 'Access denied.';
      else if (err.response?.data?.error) errorMsg = err.response.data.error;
      setCreateError(errorMsg);
    } finally {
      setCreating(false);
    }
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setCreateError(null);
    setFormData({ title: '', description: '', start: '', end: '', type: 'meeting', isPublic: true });
    setPhotoFiles([]);
    setPhotoPreviews([]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <GuestAuthBanner message="Sign up or log in to create events, join teams, and participate in activities!" />
        <div className="mb-12">
          <div className="text-center mb-6">
            <h1 className="text-5xl font-extrabold bg-gradient-to-r from-emerald-600 via-green-600 to-teal-500 bg-clip-text text-transparent mb-4">Events</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">View all public events and announcements</p>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-4">
            <button
              onClick={() => setViewMode(viewMode === 'calendar' ? 'list' : 'calendar')}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
            >
              {viewMode === 'calendar' ? (<><List className="h-5 w-5" />List View</>) : (<><CalendarDays className="h-5 w-5" />Calendar View</>)}
            </button>
            {user && (
              <button onClick={() => setShowCreateModal(true)} className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-blue-600 text-white rounded-lg shadow-lg hover:shadow-xl transition-all">
                <Plus className="h-5 w-5" />Create Event
              </button>
            )}
          </div>
        </div>

        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input type="text" placeholder="Search events..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
              </div>
              <button onClick={() => setShowFilters(!showFilters)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
                <Filter className="h-5 w-5" />Filters
                {hasActiveFilters && <span className="bg-emerald-600 text-white text-xs rounded-full px-2 py-0.5">{[searchQuery && '1', selectedType !== 'all' && '1', dateFilter !== 'all' && '1'].filter(Boolean).length}</span>}
              </button>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="px-4 py-2 text-gray-600 hover:text-gray-800 flex items-center gap-2">
                  <X className="h-5 w-5" />Clear
                </button>
              )}
            </div>
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Event Type</label>
                  <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500">
                    <option value="all">All Types</option>
                    {EVENT_TYPES.map(type => <option key={type} value={type}>{getTypeLabel(type)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                  <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value as 'all' | 'upcoming' | 'past')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500">
                    <option value="all">All Events</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="past">Past</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        <section>
          {!eventsLoading && !eventsError && (
            <div className="mb-4 text-sm text-gray-600">
              {events.length === allEvents.length ? `Showing all ${events.length} events` : `Showing ${events.length} of ${allEvents.length} events`}
            </div>
          )}
          {eventsLoading ? (
            <div className="flex justify-center py-16 bg-white rounded-2xl">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Discovering events...</p>
              </div>
            </div>
          ) : eventsError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-600 font-semibold mb-4">{eventsError}</p>
              <button onClick={() => window.location.reload()} className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Try Again</button>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-gray-300">
              <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-lg text-gray-500">{hasActiveFilters ? 'No events match your filters' : 'No events available'}</p>
              {hasActiveFilters && <button onClick={clearFilters} className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg">Clear Filters</button>}
            </div>
          ) : viewMode === 'calendar' ? (
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
              <div className="px-6 pt-6 pb-4 bg-gradient-to-r from-emerald-50 to-green-50 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 mb-1">Calendar</h2>
                <p className="text-sm text-gray-600">{events.length} {events.length === 1 ? 'event' : 'events'}</p>
              </div>
              <div className="p-4">
                <FullCalendar plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]} initialView="dayGridMonth" height="auto"
                  events={events.map(e => ({ id: e._id, title: e.title, start: e.start, end: e.end, backgroundColor: getEventColor(e), borderColor: getEventColor(e), textColor: '#ffffff', extendedProps: { type: e.type, description: e.description, isPublic: e.isPublic } }))}
                  eventClick={handleCalendarEventClick} headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay' }}
                  eventDisplay="block" locale={enLocale} firstDay={1} nowIndicator={true}
                  slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
                  eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }} />
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-2">Legend:</p>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {EVENT_TYPES.map(type => (
                    <div key={type} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: EVENT_TYPE_COLORS[type] }} />
                      <span className="text-xs text-gray-600">{getTypeLabel(type)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map(event => (
                <div key={event._id} role="button" tabIndex={0} onClick={() => handleEventClick(event._id)} onKeyDown={(e) => handleKeyDown(e, event._id)}
                  className="group bg-white rounded-2xl shadow-lg border-2 border-gray-200 overflow-hidden hover:shadow-xl hover:border-emerald-400 transition-all cursor-pointer"
                  style={{ borderLeftWidth: '4px', borderLeftColor: getEventColor(event) }}>
                  {(event.images?.[0] || event.image) && (
                    <div className="aspect-video w-full overflow-hidden bg-gray-100">
                      <img src={getImageUrl(event.images?.[0] || event.image || '') || ''} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-bold text-xl text-gray-900 group-hover:text-emerald-700 mb-2 line-clamp-1">{event.title}</h3>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getTypeColor(event.type)}`}>{getTypeLabel(event.type)}</span>
                          {event.isPublic ? (
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 flex items-center gap-1"><Globe className="h-3 w-3" />Public</span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 flex items-center gap-1"><Lock className="h-3 w-3" />Private</span>
                          )}
                        </div>
                      </div>
                      <div className="w-4 h-4 rounded-full flex-shrink-0 ml-2" style={{ backgroundColor: getEventColor(event) }} title="Event color" />
                    </div>
                    {event.description && <p className="text-sm text-gray-600 mb-4 line-clamp-2">{event.description}</p>}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock className="h-4 w-4 text-emerald-600" />
                        <span className="font-medium">Start</span>
                        <span className="truncate">{formatEventDate(event.start)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock className="h-4 w-4 text-blue-600" />
                        <span className="font-medium">End</span>
                        <span className="truncate">{formatEventDate(event.end)}</span>
                      </div>
                    </div>
                    {event.creator && (
                      <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-blue-600 text-white text-xs font-bold flex items-center justify-center">{event.creator.name[0].toUpperCase()}</div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">Created by</p>
                          <p className="text-sm font-semibold text-gray-900 truncate">{event.creator.name}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
              <div className="flex justify-between items-center border-b border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-800">New Event</h3>
                <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
              </div>
              <form onSubmit={handleCreateEvent} className="p-6 space-y-4 overflow-y-auto">
                {createError && <div className="bg-red-50 border border-red-200 rounded-lg p-3"><p className="text-red-600 text-sm">{createError}</p></div>}
                <input name="title" placeholder="Title" required value={formData.title} onChange={handleFormChange} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
                <textarea name="description" placeholder="Description" value={formData.description} onChange={handleFormChange} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 h-20" />
                <input type="datetime-local" name="start" required value={formData.start} onChange={handleFormChange} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
                <input type="datetime-local" name="end" required value={formData.end} onChange={handleFormChange} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
                <select name="type" value={formData.type} onChange={handleFormChange} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500">
                  {EVENT_TYPES.map(type => <option key={type} value={type}>{getTypeLabel(type)}</option>)}
                </select>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Photos</label>
                  {photoPreviews.length > 0 && (
                    <div className="flex gap-2 flex-wrap mb-2">
                      {photoPreviews.map((src, i) => (
                        <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-300">
                          <img src={src} alt="" className="w-full h-full object-cover" />
                          <button type="button" onClick={() => handleRemovePhoto(i)} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <input type="file" id="event-photo-upload" accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" multiple onChange={handlePhotoChange} className="hidden" />
                  <label htmlFor="event-photo-upload" className="cursor-pointer inline-flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-emerald-400 hover:bg-emerald-50 transition-colors">
                    <ImageIcon className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Add Photos</span>
                  </label>
                  <p className="text-xs text-gray-500 text-center mt-1">PNG, JPG, GIF, WebP up to 5MB each</p>
                </div>
                <label className="flex items-center space-x-2">
                  <input name="isPublic" type="checkbox" checked={formData.isPublic} onChange={handleFormChange} className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                  <span className="text-sm text-gray-700">Public</span>
                </label>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={handleCloseModal} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700">Cancel</button>
                  <button type="submit" disabled={creating} className="flex-1 bg-gradient-to-r from-emerald-500 to-green-500 text-white py-2 rounded-lg hover:opacity-90 disabled:opacity-50">
                    {creating ? 'Creating...' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicEventsPage;
